// @flow
import React, { useContext, useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import find from 'ramda/es/find';
import head from 'ramda/es/head';
import isEmpty from 'ramda/es/isEmpty';
import prop from 'ramda/es/prop';

import Modal, { ModalHeader, ModalContent, ModalFooter } from 'app/common/components/Modal';
import ControlsBar from 'app/common/ui-next/controls-bar';
import PlainButton from 'app/common/ui-next/plain-button';
import Loading from 'app/common/components/Loader';
import { Table } from 'app/common/ui-components/table';
import { popupErr } from 'app/common/actions/popup';
import { employeeFetch } from 'app/actions/users/employees';
import { studentFetch } from 'app/actions/users/students';

import { OrderContext, type ReceiverItem } from '../../../OrderData';
import TargetSelectModalItem from './TargetSelectModalItem';
import styles from './TargetSelectModal.module.scss';;


type Props = {
  toggleModal: () => void,
  onChangeReceiver?: (receiver: ReceiverItem) => void,
};

const TargetSelectModal = ({
  toggleModal,
  onChangeReceiver,
}: Props) => {
  const { t } = useTranslation('shop');
  const dispatch = useDispatch();

  const {
    content: { receivers },
    request,
    loading: { receivers: loadingReceivers },
  } = useContext(OrderContext);

  const [currentReceiver, setCurrentReceiver] = useState<ReceiverItem | null>(null);

  /**
  * Запрос получателей и начальная инициализация
  */

  useEffect(() => {
    request.receivers();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (receivers && !isEmpty(receivers)) {
      setCurrentReceiver(head(receivers));
    }
  }, [receivers]);


  const handleChangeTarget = useCallback((e: SyntheticEvent<HTMLInputElement>) => {
    const { value, checked } = e.currentTarget;
    if (checked) {
      const targetId = parseInt(value, 10);
      const receiverBySelectedId = find(({ id }) => (id === targetId), receivers);
      if (receiverBySelectedId) {
        setCurrentReceiver(receiverBySelectedId);
      }
    }
  }, [receivers]);

  const handleReceiverSubmit = useCallback((e: SyntheticInputEvent<HTMLFormElement>) => {
    const { id: formId } = e.target;
    if (formId !== 'receiverSelect') return;
    e.preventDefault();
    if (onChangeReceiver && currentReceiver) {
      onChangeReceiver(currentReceiver);
    }
  }, [onChangeReceiver, currentReceiver]);

  const handleRequestEmployee = useCallback(async (id: number) => {
    if ((process.env.APP_NAME || process.env.REACT_APP_NAME) !== 'journal') {
      const errorText = 'Не предусмотрена функция запроса сотрудника для этого кабинета';
      dispatch(popupErr(errorText));
      throw new Error(errorText);
    }

    const res = await dispatch(employeeFetch(id));
    const { photoUrl = '', postTitle = '', roleTitle = '' } = prop(String(id), res) || {};

    return ({
      id,
      photoUrl,
      postTitle: postTitle || roleTitle,
    });
  }, [dispatch]);

  const handleRequestStudent = useCallback(async (id: number) => {
    if ((process.env.APP_NAME || process.env.REACT_APP_NAME) !== 'journal') {
      const errorText = 'Не предусмотрена функция запроса ученика для этого кабинета';
      dispatch(popupErr(errorText));
      throw new Error(errorText);
    }

    const res = await dispatch(studentFetch(id));
    const { photoUrl = '', klass = '' } = prop(String(id), res) || {};

    return ({
      id,
      photoUrl,
      classTitle: klass,
    });
  }, [dispatch]);

  return (
    <Modal toggleModal={toggleModal} className={styles.root}>
      <form onSubmit={handleReceiverSubmit} id="receiverSelect">
        <ModalHeader>{t('target_select_modal_header')}</ModalHeader>
        <ModalContent>
          <div>{t('target_select_modal_select_label')}</div>
        </ModalContent>
        <ModalContent maxHeight={300} className={styles.tableContent}>
          {loadingReceivers
            ? <Loading />
            : <Table className={styles.table}>
              <tbody>
                {receivers.map(({ name, id, type }) => (
                  <TargetSelectModalItem
                    t={t}
                    key={id}
                    type={type}
                    id={id}
                    name={name}
                    currentTargetId={currentReceiver ? currentReceiver.id : 0}
                    onClickTarget={handleChangeTarget}
                    onRequestEmployee={handleRequestEmployee}
                    onRequestStudent={handleRequestStudent}
                  />
                ))}
              </tbody>
            </Table>}
        </ModalContent>
        <ModalFooter>
          <ControlsBar right>
            <PlainButton outline onClick={toggleModal}>
              {t('target_select_modal_bt_cancel')}
            </PlainButton>
            <PlainButton type="submit" disabled={!currentReceiver}>
              {t('target_select_modal_bt_submit')}
            </PlainButton>
          </ControlsBar>
        </ModalFooter>
      </form>
    </Modal>
  );
};

export default TargetSelectModal;
